import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout, AuthStatus } from '../components/AuthLayout';
import { InlineError, SkeletonText } from '../components/Feedback';
import { TextField } from '../components/Form';
import { Chip } from '../components/Chips';
import { PasswordField, PasswordStrength } from '../components/Password';
import { useAuth } from '../lib/auth';
import { invitations } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { assessPassword, usePasswordPolicy } from '../lib/password';
import { GatewayRoleDescriptions, GatewayRoleLabels } from '../lib/enums';
import { errorMessage } from '../lib/api';
import { useToast } from '../lib/toast';

export function AcceptInvitationPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { adoptSession } = useAuth();
  const policy = usePasswordPolicy();

  const token = params.get('token') ?? '';

  const preview = useAsync(
    (signal) =>
      token === ''
        ? Promise.resolve(null)
        : invitations.preview(token, signal).then((result) => result ?? null),
    [token],
  );

  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the name field from the address the admin invited, so most people just click through.
  useEffect(() => {
    const email = preview.data?.email;
    if (!email || displayName !== '') return;
    const local = email.split('@')[0] ?? '';
    const guess = local
      .split(/[._-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
    if (guess !== '') setDisplayName(guess);
    // Only ever runs once per preview; the guard above keeps user edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.data]);

  const assessment = assessPassword(password, policy);
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit =
    token !== '' && displayName.trim() !== '' && assessment.satisfied && confirm === password;

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);
    setError(null);
    if (!canSubmit) return;

    setPending(true);
    try {
      const response = await invitations.accept({
        token,
        displayName: displayName.trim(),
        password,
      });
      if (!response?.accessToken || !response.user) {
        setError('The invitation was accepted but no session was issued. Please sign in.');
        return;
      }
      adoptSession(response.accessToken, response.user);
      toast.success('Welcome aboard', `You joined ${response.user.tenantName}.`);
      navigate('/', { replace: true });
    } catch (cause) {
      const message = errorMessage(cause);
      setError(message);
      toast.error('Could not accept the invitation', message);
    } finally {
      setPending(false);
    }
  };

  const backLinks = (
    <>
      <Link to="/signin">Sign in</Link>
      <Link to="/signup">Create a new account</Link>
    </>
  );

  if (token === '') {
    return (
      <AuthLayout title="Join your team" subtitle="This link is missing its invitation token." footer={backLinks}>
        <AuthStatus tone="warn" title="Nothing to accept">
          <p>
            Open the link straight from the invitation email. If it still fails, ask your
            administrator to send a fresh invitation.
          </p>
        </AuthStatus>
      </AuthLayout>
    );
  }

  if (preview.loading && !preview.data) {
    return (
      <AuthLayout title="Join your team" subtitle="Checking your invitation…">
        <div aria-busy="true">
          <SkeletonText lines={4} />
        </div>
      </AuthLayout>
    );
  }

  if (preview.error) {
    return (
      <AuthLayout title="Join your team" subtitle="We could not check that invitation." footer={backLinks}>
        <AuthStatus
          tone="bad"
          title="The invitation could not be read"
          actions={
            <button type="button" className="btn btn-secondary" onClick={preview.reload}>
              Try again
            </button>
          }
        >
          <p>{preview.error}</p>
        </AuthStatus>
      </AuthLayout>
    );
  }

  const invitation = preview.data;

  if (!invitation || !invitation.valid) {
    return (
      <AuthLayout title="Join your team" subtitle="That invitation is no longer usable." footer={backLinks}>
        <AuthStatus tone="bad" title="This invitation cannot be used">
          <p>
            {invitation?.message ??
              'The invitation has expired, been revoked, or has already been accepted.'}
          </p>
          <p className="auth-status-hint">
            Ask an administrator on your team to invite you again. If you already have an account,
            just sign in.
          </p>
        </AuthStatus>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={`Join ${invitation.companyName ?? 'your team'}`}
      subtitle="Set a password and you are in."
      footer={backLinks}
      note="Your administrator chose your role. They can change it later in Settings."
    >
      <dl className="auth-summary">
        <div className="auth-summary-row">
          <dt>Company</dt>
          <dd>{invitation.companyName ?? '—'}</dd>
        </div>
        <div className="auth-summary-row">
          <dt>Email</dt>
          <dd>{invitation.email ?? '—'}</dd>
        </div>
        <div className="auth-summary-row">
          <dt>Role</dt>
          <dd>
            {invitation.role === null ? (
              '—'
            ) : (
              <>
                <Chip tone="info">{GatewayRoleLabels[invitation.role]}</Chip>
                <span className="auth-summary-hint">{GatewayRoleDescriptions[invitation.role]}</span>
              </>
            )}
          </dd>
        </div>
      </dl>

      <form className="signin-form" onSubmit={onSubmit} noValidate>
        <TextField
          label="Your name"
          value={displayName}
          onChange={setDisplayName}
          required
          autoComplete="name"
          placeholder="Jane Bennet"
          error={submitted && displayName.trim() === '' ? 'Tell us what to call you.' : null}
        />
        <PasswordField
          label="Password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
          error={
            submitted && !assessment.satisfied ? 'This password does not meet the policy.' : null
          }
        />
        <PasswordField
          label="Confirm password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          required
          error={
            mismatch || (submitted && confirm !== password)
              ? 'The two passwords do not match.'
              : null
          }
        />

        <PasswordStrength password={password} policy={policy} />

        <InlineError message={error} />

        <button type="submit" className="btn btn-primary btn-block" disabled={pending}>
          {pending ? 'Joining…' : 'Accept invitation'}
        </button>
      </form>
    </AuthLayout>
  );
}
